import {POPUP} from "/@back@/commons/widgets/popups.js";
import {REG} from "/@lib@/commons/registry.js";
import {Action, IAction} from "/@lib@/commons/actions.js";
import {BaseElement, OSkinableInit} from "/@back@/commons/basis.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {ActionBtn, Button, ButtonToggle} from "/@back@/commons/widgets/buttons.js";
import {IError} from "/@lib@/commons/errorLog.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {CLIPBOARD} from "/@lib@/commons/clipboard.js";

/**
 *
 */
export interface ErrorViewer extends BaseElement {
	initialize(init?: OErrorViewerInit): this
}

export interface OErrorViewerInit extends OSkinableInit {
	error: IError;
}

export class ErrorViewer extends BaseElement {
	protected _reportCo: HTMLElement;
	protected _showReportBtn: ButtonToggle;
	protected _technicalCo: HTMLElement;
	protected _showTechnicalBtn: ButtonToggle;

	protected _initialize(init: OErrorViewerInit) {
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		const reg = this.findReg(init);
		reg.installSkin("webzone:panel", sr);
		reg.installSkin("standard-dialog", sr);
		reg.installSkin("scroll/small", sr);
		this._initAndInstallSkin('c-error-viewer', init);
		sr.appendChild(<div id="message">{init.error.msg}</div>);

		const main = sr.appendChild(<div id="main"/>);
		if (init.error.details) {
			main.appendChild(<div id="details">{init.error.details}</div>);
			DOM.addClass(this, 'details');
		}

		if (init.error.technicalDetails) {
			this._showTechnicalBtn = main.appendChild(new ButtonToggle().initialize({uiContext: "dialog", label: "Afficher le détail technique"}));
			this._technicalCo = main.appendChild(<div id="technicalCo" hidden="">{init.error.technicalDetails}</div>);
			this._showTechnicalBtn.onclick = function (this: ButtonToggle) {
				const self = DOMSH.findHost<ErrorViewer>(this);
				if (!this.toggleOn) {
					this.toggleOn = true;
					self._technicalCo.hidden = false;
					self._reportCo.hidden = true;
					self._showReportBtn.toggleOn = false;
					DOM.addClass(self, 'report');
				} else {
					this.toggleOn = false;
					self._technicalCo.hidden = true;
					DOM.removeClass(self, 'report');
				}
			};
		}
		this._showReportBtn = main.appendChild(new ButtonToggle().initialize({uiContext: "dialog", label: "Afficher le rapport complet"}));
		this._reportCo = main.appendChild(<div id="reportCo" hidden="">{ERROR.getReport(init.error)}</div>);
		this._showReportBtn.onclick = function (this: ButtonToggle) {
			const self = DOMSH.findHost<ErrorViewer>(this);
			if (!this.toggleOn) {
				this.toggleOn = true;
				self._reportCo.hidden = false;
				if (self._technicalCo) {
					self._technicalCo.hidden = true;
					self._showTechnicalBtn.toggleOn = false;
				}
				DOM.addClass(self, 'report');
			} else {
				this.toggleOn = false;
				self._reportCo.hidden = true;
				DOM.removeClass(self, 'report');
			}
		};


		const footer = sr.appendChild(<div id="footer"/>);
		const actionContext = {error: init.error, emitter: this};
		for (const action of REG.reg.getList<IAction<IErrorActionCtx>>('actions:error')) {
			footer.appendChild(new ActionBtn<IErrorActionCtx>().initialize({action, actionContext, uiContext: 'dialog'}));
		}
		const closeBtn = footer.appendChild(new Button().initialize({uiContext: "dialog", label: "Fermer"}));
		closeBtn.onclick = function (this: Button) {
			POPUP.findPopupableParent(this).close();
		};
	}
}

REG.reg.registerSkin('c-error-viewer', 1, /* language=CSS */ `
	:host(.report) {
		height: 80vh;
	}

	#message {
		font-weight: 1.5em;
		font-weight: bold;
		text-align: center;
		margin: 1em;
	}

	#main {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		flex: 1;
		margin: 1em;
	}

	:host(.report) #main {
		min-height: 10em;
	}

	#details {
		white-space: pre;
		padding: 1em;
	}

	c-button-toggle {
		font-size: 0.9em;
		margin: 0.3em 0.5em;
	}

	#reportCo, #technicalCo {
		flex: 1;
		overflow: auto;
		background-color: var(--edit-bgcolor);
		white-space: pre;
		padding: 1em;
	}

	.msgList {
		padding-inline-start: 1em;
		margin: 0;
	}

	.error {
		color: var(--error-color);
	}

	.warn {
		color: var(--warning-color);
	}
`);

customElements.define('c-error-viewer', ErrorViewer);

/**
 *
 */
export interface IErrorActionCtx {
	error: IError,
	emitter: HTMLElement
}

export let copyReportAction = new Action<IErrorActionCtx>()
	.setLabel("Copier le rapport")
	.setExecute(function (ctx) {
		return CLIPBOARD.writeInClipboard(async () => {
			await navigator.clipboard.writeText(ERROR.getReport(ctx.error));
			POPUP.showNotifInfo("Le rapport a été copié dans le presse-papier.", ctx.emitter);
		}, ctx.emitter);
	});
REG.reg.addToList("actions:error", "copyReport", 1, copyReportAction, 0);

